import { useCallback } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { datasourceEndpoints, documentsEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPatch, callPost, callPut } from '@/api/fetcher';
import { getDebouncedInvalidator } from '@/hooks/utils/debounceQueryInvalidation';
import { CollectionDocument, PartialDocument } from '@/shared/types/collections';
import { Pagination } from '@/shared/types/common';

import useDatasources from '../../../hooks/useDatasources';
import { DATASOURCE_TYPE_TO_TAB, ROOT_DATASOURCE_NAMES } from '../constants';
import {
  onDatasourcesDocumentsCreated,
  onDatasourcesDocumentsDeleted,
  onDatasourcesDocumentsUpdated,
} from './useAPIDatasourcesDocuments';
import { useTabCategory } from './useCategory';
import { onDocumentContentUpdated } from './useDocumentContent';
import { queryKeyFactories } from '../../../hooks/utils/queryKeyFactory';

/**
 * Type for the API response that contains datasources with document fields and pagination
 */
interface DatasourcesAndDocumentsResponse {
  documents: CollectionDocument[];
  pagination: Pagination;
}

/**
 * Hook parameters interface
 */
interface UseAPIDocumentsParams {
  collection_id: string;
  datasource_id?: string;
  document_id?: string;
  enabled?: boolean;
}

/**
 * API methods for the hook
 */
const API = Object.freeze({
  createDocument: async (
    collectionId: string,
    datasourceId: string,
    document: Partial<CollectionDocument>
  ): Promise<CollectionDocument> =>
    callPost(documentsEndpoints.documents(collectionId, datasourceId), document),
  getDocument: async (
    collectionId: string,
    datasourceId: string,
    document_id: string
  ): Promise<CollectionDocument> =>
    callGet(documentsEndpoints.document(collectionId, datasourceId, document_id)),
  listDocuments: async (
    collection_id: string,
    datasource_id: string
  ): Promise<DatasourcesAndDocumentsResponse> => {
    return callGet(documentsEndpoints.listDocuments(collection_id, datasource_id));
  },
  updateDocument: async (
    collectionId: string,
    datasource_id: string,
    document: PartialDocument
  ): Promise<CollectionDocument> =>
    callPut(
      documentsEndpoints.document(collectionId, datasource_id, document.document_id),
      document
    ),
  deleteDocument: async (
    collectionId: string,
    datasource_id: string,
    document_id: string
  ): Promise<CollectionDocument> =>
    callDelete(documentsEndpoints.document(collectionId, datasource_id, document_id)),
  moveDocument: async (
    collectionId: string,
    datasourceId: string,
    documentId: string,
    newDatasourceId: string
  ): Promise<CollectionDocument> =>
    callPatch(documentsEndpoints.moveDocument(collectionId, datasourceId, documentId), {
      new_datasource_id: newDatasourceId,
    }),
});

export const onDocumentUpdated = (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  document_id: string,
  document: Partial<CollectionDocument>
) => {
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);

  queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }
    const updatedDocuments = oldData.documents.map((oldDocument) =>
      oldDocument.document_id === document_id ? { ...oldDocument, ...document } : oldDocument
    );
    return { ...oldData, documents: updatedDocuments };
  });

  queryClient.setQueryData<CollectionDocument>(
    [documentsEndpoints.document(collection_id, datasource_id, document_id)],
    (oldData) => (oldData ? { ...oldData, ...document } : oldData)
  );
};

/**
 * A datasources:updated event can stand for many document changes the server does not send one by
 * one (enable/disable all), so reload the datasource's documents once a burst of events settles.
 */
export const reloadDatasourceDocuments = (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string
) => {
  const invalidator = getDebouncedInvalidator(queryClient);
  // The folder view, the collection's datasource tables (root FAQ and document tabs), the
  // publish modal and the draft counter all show document state these changes alter.
  invalidator.invalidate(queryKeyFactories.folderDocuments(collection_id, datasource_id));
  invalidator.invalidate(queryKeyFactories.collectionDatasourcesDocuments(collection_id));
  invalidator.invalidate(queryKeyFactories.publishDocuments(collection_id));
  invalidator.invalidate(queryKeyFactories.draftDocumentsCount(collection_id));
};

/**
 * Publish and draft change documents across a collection's datasources in one statement, and the
 * server says so with a single collections:updated rather than an event per datasource. Reload
 * every list of the collection's documents: only the ones on screen refetch.
 */
export const reloadCollectionDocuments = (queryClient: QueryClient, collection_id: string) => {
  const invalidator = getDebouncedInvalidator(queryClient);
  // Prefix of every folderDocuments(collection_id, datasource_id) key
  invalidator.invalidate(['folder-documents', collection_id]);
  invalidator.invalidate(queryKeyFactories.collectionDatasourcesDocuments(collection_id));
  invalidator.invalidate(queryKeyFactories.publishDocuments(collection_id));
  invalidator.invalidate(queryKeyFactories.draftDocumentsCount(collection_id));
};

export const onRTMDocumentUpdated = async (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  document_id: string
) => {
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);

  // Check if query is in cache before fetching
  if (!queryClient.getQueryData(queryKey)) {
    return;
  }

  try {
    const newDocument = await API.getDocument(collection_id, datasource_id, document_id);

    queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }

      const updatedDocuments = oldData.documents.map((oldDocument) =>
        oldDocument.document_id === document_id ? { ...oldDocument, ...newDocument } : oldDocument
      );
      return { ...oldData, documents: updatedDocuments };
    });
  } catch (error) {
    // Silently fail as requested
    console.error('Failed to fetch document during RTM update:', error);
  }
};

export const onRTMDocumentCreated = async (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  document_id: string
) => {
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);

  // Check if query is in cache before fetching
  if (!queryClient.getQueryData(queryKey)) {
    return;
  }

  try {
    const newDocument = await API.getDocument(collection_id, datasource_id, document_id);

    queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return { ...oldData, documents: [...oldData.documents, newDocument] };
    });
  } catch (error) {
    // Silently fail as requested
    console.error('Failed to fetch document during RTM create:', error);
  }
};

export const onDocumentCreated = (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  document: CollectionDocument
) => {
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);
  queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }

    return { ...oldData, documents: [...oldData.documents, document] };
  });
};

export const onDocumentDeleted = (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  document_id: string
) => {
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);

  queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }

    return {
      ...oldData,
      documents: oldData.documents.filter((d) => d.document_id !== document_id),
    };
  });
};

/**
 * Custom React hook for managing datasources and documents for collections using React Query
 *
 * This hook provides a unified interface for working with datasources and documents across
 * four different categories: FAQ, documents, files, and external sources. It uses React Query
 * for caching and state management with pessimistic updates.
 *
 * @param collection_id - The collection identifier
 * @param datasource_id - The datasource identifier
 * @param enabled - Whether the query should be enabled (defaults to true)
 *
 */
export const useAPIDocuments = ({
  collection_id,
  datasource_id,
  document_id,
  enabled = true,
}: UseAPIDocumentsParams) => {
  // Generate query key
  const queryKey = queryKeyFactories.folderDocuments(collection_id, datasource_id);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { category } = useTabCategory();

  // Get datasources to determine if a datasource is root or folder
  const { datasources } = useDatasources(collection_id);

  // Helper function to determine if a datasource is a root datasource
  const isRootDatasource = (datasourceId: string) => {
    if (!datasources) {
      return false;
    }
    const datasource = datasources.find((ds) => ds.datasource_id === datasourceId);
    if (!datasource) {
      return false;
    }
    const rootNames = Object.values(ROOT_DATASOURCE_NAMES) as string[];
    return rootNames.includes(datasource.name);
  };

  // Fetch data
  const {
    data: response,
    isLoading,
    status: listStatus,
  } = useQuery<DatasourcesAndDocumentsResponse, Error>({
    queryKey,
    queryFn: () => API.listDocuments(collection_id, datasource_id),
    enabled: !!collection_id && !!datasource_id && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const { data: document, status: getStatus } = useQuery<CollectionDocument, Error>({
    queryKey: [documentsEndpoints.document(collection_id, datasource_id, document_id)],
    queryFn: () => API.getDocument(collection_id, datasource_id, document_id),
    enabled: !!collection_id && !!datasource_id && !!document_id,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const { mutate: createDocument, mutateAsync: createDocumentAsync } = useMutation<
    CollectionDocument,
    Error,
    Partial<CollectionDocument>
  >({
    mutationFn: (data) =>
      API.createDocument(collection_id, datasource_id || data.datasource_id, data),
    onSuccess: (resp) => {
      onDocumentCreated(queryClient, collection_id, resp.datasource_id, resp);
      onDatasourcesDocumentsCreated(
        queryClient,
        collection_id,
        category,
        resp.datasource_id,
        resp.document_id
      );
      onDatasourcesDocumentsUpdated(
        queryClient,
        collection_id,
        category,
        resp.datasource_id,
        resp.document_id
      );
      // Invalidate the allDatasources query to update the content column in the knowledge bases table
      queryClient.invalidateQueries({
        queryKey: [datasourceEndpoints.allDatasources(collection_id)],
      });
      toast.success(t('collections.documents.created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });
  const { mutate: updateDocument, mutateAsync: updateDocumentAsync } = useMutation<
    CollectionDocument,
    Error,
    PartialDocument
  >({
    mutationFn: (data) =>
      API.updateDocument(collection_id, datasource_id || data.datasource_id, data),
    onSuccess: (resp) => {
      onDocumentUpdated(queryClient, collection_id, resp.datasource_id, resp.document_id, resp);
      onDocumentContentUpdated(queryClient, collection_id, resp.datasource_id, resp.document_id);
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.datasourcesDocuments(collection_id, category),
      });

      toast.success(t('collections.documents.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: moveDocumentAPI } = useMutation<
    CollectionDocument,
    Error,
    { document_id: string; new_datasource_id: string; datasource_id: string }
  >({
    mutationFn: (data) =>
      API.moveDocument(
        collection_id,
        datasource_id || data.datasource_id,
        data.document_id,
        data.new_datasource_id
      ),
    onSuccess: (resp, data) => {
      const sourceDatasourceId = datasource_id || data.datasource_id;
      const destinationDatasourceId = data.new_datasource_id;

      // Find datasource information to determine correct categories
      const sourceDatasource = datasources?.find((ds) => ds.datasource_id === sourceDatasourceId);
      const destinationDatasource = datasources?.find(
        (ds) => ds.datasource_id === destinationDatasourceId
      );

      // Determine categories based on datasource types
      const sourceCategory = sourceDatasource
        ? DATASOURCE_TYPE_TO_TAB[sourceDatasource.type]
        : category;
      const destinationCategory = destinationDatasource
        ? DATASOURCE_TYPE_TO_TAB[destinationDatasource.type]
        : category;

      // Determine if source and destination are root datasources or folders
      const isSourceRoot = isRootDatasource(sourceDatasourceId);
      const isDestinationRoot = isRootDatasource(destinationDatasourceId);

      // Remove from source cache
      if (isSourceRoot) {
        // Remove from root datasources documents cache
        onDatasourcesDocumentsDeleted(
          queryClient,
          collection_id,
          sourceCategory,
          sourceDatasourceId,
          data.document_id
        );
      } else {
        // Remove from folder documents cache
        onDocumentDeleted(queryClient, collection_id, sourceDatasourceId, data.document_id);
      }
      // Add to destination cache
      if (isDestinationRoot) {
        // Add to root datasources documents cache
        onDatasourcesDocumentsCreated(
          queryClient,
          collection_id,
          destinationCategory,
          destinationDatasourceId,
          resp.document_id
        );
      } else {
        // Add to destination folder documents cache
        onDocumentCreated(queryClient, collection_id, destinationDatasourceId, resp);
      }

      // Update the source datasource cache in order to update the document count
      // when pressing move to folder
      onDatasourcesDocumentsUpdated(
        queryClient,
        collection_id,
        destinationCategory,
        destinationDatasourceId
      );

      // Update the destination datasource cache in order to update the document count
      // when pressing remove from folder
      onDatasourcesDocumentsUpdated(
        queryClient,
        collection_id,
        destinationCategory,
        sourceDatasourceId
      );

      toast.success(t('collections.documents.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });
  const {
    mutate: deleteDocument,
    mutateAsync: deleteDocumentAsync,
    status: deleteStatus,
  } = useMutation<
    Pick<CollectionDocument, 'document_id'>,
    Error,
    { document_id: string; datasource_id?: string }
  >({
    mutationFn: (data) =>
      API.deleteDocument(collection_id, data.datasource_id || datasource_id, data.document_id),
    onSuccess: (resp, data) => {
      onDocumentDeleted(
        queryClient,
        collection_id,
        data.datasource_id || datasource_id,
        resp.document_id
      );
      onDatasourcesDocumentsDeleted(queryClient, collection_id, category, null, resp.document_id);
      onDatasourcesDocumentsUpdated(
        queryClient,
        collection_id,
        category,
        data.datasource_id || datasource_id,
        resp.document_id
      );
      // Invalidate the allDatasources query to update the content column in the knowledge bases table
      queryClient.invalidateQueries({
        queryKey: [datasourceEndpoints.allDatasources(collection_id)],
      });
      toast.success(t('collections.documents.deleted'));
    },
    meta: { errorToast: true },
  });

  const getDocumentById = (document_id: string) => {
    if (!document_id) {
      return;
    }

    return response?.documents.find((document) => document.document_id === document_id);
  };

  const moveDocument = async (data: {
    document_id: string;
    new_datasource_id: string;
    datasource_id: string;
    name: string;
  }) => {
    // Get documents from the destination datasource to check for duplicates
    const destinationQueryKey = queryKeyFactories.folderDocuments(
      collection_id,
      data.new_datasource_id
    );
    const destinationData =
      queryClient.getQueryData<DatasourcesAndDocumentsResponse>(destinationQueryKey);
    if (destinationData?.documents) {
      // Check if any document in the destination datasource has the same name
      const duplicateDocument = destinationData.documents.find(
        (doc) => doc.name === data.name && doc.document_id !== data.document_id
      );

      if (duplicateDocument) {
        toast.error(t('collections.documents.duplicateNameError', { 0: data.name }));
        return;
      }
    }

    // Proceed with the move if no duplicates found
    moveDocumentAPI(data);
  };

  const updateFolderDocuments = useCallback(
    (datasource_id: string) => {
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.folderDocuments(collection_id, datasource_id),
        refetchType: 'inactive',
      });
    },
    [collection_id, queryClient]
  );

  /**
   * Returns the names of documents in a datasource, with file extensions removed if present.
   *
   * @param datasource_id - The datasource identifier
   * @returns Array of document names without file extensions
   *
   * @example
   * // If a document is named "file-sample_100kB.docx", returns "file-sample_100kB"
   */
  const getDatasourceDocumentsNames = useCallback(
    (datasource_id: string): string[] | undefined => {
      return response?.documents
        .filter((doc) => doc.datasource_id === datasource_id)
        .map((doc) => {
          // Remove file extension if present (e.g., ".docx")
          const lastDotIndex = doc.name.lastIndexOf('.');
          if (lastDotIndex > 0) {
            return doc.name.substring(0, lastDotIndex);
          }
          return doc.name;
        });
    },
    [response]
  );

  return {
    data: response?.documents || [],
    isLoading,
    listStatus,
    updateDocument,
    updateDocumentAsync,
    deleteDocument,
    deleteDocumentAsync,
    deleteStatus,
    createDocument,
    createDocumentAsync,
    document,
    getStatus,
    moveDocument,
    getDocumentById,
    updateFolderDocuments,
    getDatasourceDocumentsNames,
  };
};
